import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Table } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';

import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { GlobalAuditHistoryVm } from 'app/shared/generated/Administration/Models/GlobalAuditHistoryVm';
import { GlobalService } from 'app/shared/services/global.service';
import { FormatHelperService } from 'app/shared/helpers/format-helper.service';
import { SharedTableModule } from 'app/shared/standalone/shared-table/shared-table.module';
import { VisibleColsService } from 'app/shared/standalone/shared-table/visible-cols.service';
import { LazyTableService } from 'app/shared/standalone/shared-table/lazy-table.service';
import { LazyLoadVm } from 'app/shared/standalone/shared-table/lazy-load-vm.interface';
import { SelectItem } from 'app/shared/models/select-item';

@Component({
    selector: 'pcg-global-audit-list'
    , templateUrl: './global-audit-list.component.html'
    , styleUrls: ['./global-audit-list.component.scss']
    , standalone: true 
    , providers: [ DecimalPipe ]
    , host: { '(window:resize)': 'onResize()' }
    , changeDetection: ChangeDetectionStrategy.Eager
    , imports: [
        SharedTableModule
        , DatePickerModule
    ]
})
export class GlobalAuditListComponent implements OnInit, AfterViewInit {

    http = inject(HttpClient);
    cdr = inject(ChangeDetectorRef);
    lts = inject(LazyTableService);
    decimalPipe = inject(DecimalPipe);
    title = inject(Title);
    public fhs = inject(FormatHelperService);

    @ViewChild('dt', { static: true }) dt: Table;

    columnDefs = GlobalAuditHistoryVm.ColumnDefs;
    lazyLoadVm: LazyLoadVm;
    lazyTableInput = this.lts.setLazyTableInput(100, "dateChanged", -1);
    availableColumns = VisibleColsService.getAvailableCols(this.columnDefs);
    visibleColumns = this.lts.getVisibleCols(this.availableColumns);
    searchColumns = this.lts.getSearchCols(this.availableColumns);
    filterChangedBy: SelectItem[] = [];
    filterDateChanged: Date[] = [ this.lts.getStartDateMonths(1), this.lts.endDate ];

    filterBusinessAreas = Object.keys(BusinessAreaEnum)
        .filter((key) => !isNaN(Number(key)))
        .map((enumValueAsString) => ({
			// Grabs display name so dropdown doesn't display concatenated columns
			text: this.parseBusinessAreaEnumToDescription(Number(enumValueAsString)),
			value: Number(enumValueAsString)
		})).slice(1); // Remove Unassigned from selectable options as it is a null placeholder for the backend
    selectedBusinessAreas = new Array();

    filters = {
		businessAreas: null
        , dateChanged: this.lts.setDateRangeFilter(this.lts.getStartDateMonths(1), this.lts.endDate)
        , changedBy: null
        , firstLoad: true
        , rx: null
	};
    initFilters = Object.assign({}, this.filters);
    filterMap = {
		"businessAreas": "Business Area"
        , "dateChanged": "Date Changed"
        , "changedBy": "Changed By"
        , "rx" : "Rx Number"
	};

    isMobile = false;
    isLoading: boolean = true;
    dataSource = `api/Administration/GlobalAuditHistory/GetGlobalAuditList`;

    tableDts = {
        root: {
            header: {
                lgPadding: "0 0 0.5rem 0"
            }
        }
    };

    onResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

    get hasFilters() { return this.lts.getActiveFilters(this.filters, this.filterMap, this.initFilters); }

    ngOnInit() {
        this.title.setTitle("Global Audit History");
        this.isMobile = GlobalService.setIsMobile(window.innerWidth); 
        this.getFilters();
    }

    ngAfterViewInit() { this.filters.firstLoad = false; }

    getFilters() {
        this.http.get("api/Select/Users").subscribe((lst: any)=> { 
            this.filterChangedBy = lst; 
            this.filterChangedBy.unshift({
                value: null
                , text: "All"
            });
        }); 
    }

    getList() { 
        this.lts.getList(
            this.dataSource
            , this.lazyTableInput
            , this.filters
        ).subscribe((lst: LazyLoadVm) => { 
            this.lazyLoadVm = lst;
            this.isLoading = false;
            this.cdr.detectChanges();
        });
    }

    loadLazy(event: any) {
        if (event?.sortField != undefined) { 
            this.lazyTableInput.sortField = event.sortField; 
            this.lazyTableInput.sortOrder = event.sortOrder;
        }
        this.getList();
    }

    clearFilters() {
        this.selectedBusinessAreas = [];
        this.filters = Object.assign(this.filters, this.initFilters);
        this.getList();
    }

    clearDateRange() {
        this.filters.dateChanged = null;
        this.filterDateChanged = null;
    }

    stringifyFilters() { 
        if (this.selectedBusinessAreas.length > 0) { this.filters.businessAreas = JSON.stringify(this.selectedBusinessAreas); }
        else { this.filters.businessAreas = null; }
    }

    parseBusinessAreaEnumToDescription(val: BusinessAreaEnum) { return BusinessAreaEnum.toDescription(val); }
}
